/**
 * Oryzo-style big-text slide: every section heading on the page glides in
 * from alternating sides and settles as it scrolls toward the centre.
 * Scroll-linked (not one-shot). Reduced motion = no effect.
 */
import { useEffect } from "react";

export default function ScrollTextReveal({ selector = "section h2" }: { selector?: string }) {
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
    els.forEach((el) => { el.style.willChange = "transform, opacity"; });
    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      els.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        // 0 when entering at the bottom, 1 once it reaches ~55% of the viewport.
        const p = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.45)));
        const ease = 1 - Math.pow(1 - p, 3);
        const dir = i % 2 === 0 ? -1 : 1;
        el.style.transform = `translate3d(${dir * (1 - ease) * 18}vw,0,0) scale(${0.92 + ease * 0.08})`;
        el.style.opacity = String(0.1 + ease * 0.9);
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      els.forEach((el) => { el.style.transform = ""; el.style.opacity = ""; el.style.willChange = ""; });
    };
  }, [selector]);
  return null;
}
